import React, { useCallback, useEffect, useRef, useState } from 'react'
import AddListingModal from '../../../common/components/AddListingModal/AddListingModal'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  type CloudAccountChangeParams,
  CloudAccountChangeV2,
  actionTypeWrapper,
  cloudTypes,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  isLoading,
  LoadingTypes,
  PartnerType,
  setSelectedAccount,
  startLoading,
  stopLoading,
  updateAppAlert,
  useAccountId,
  useCloudAccounts,
  useCurrentCloudProvider,
  useUserType,
} from '@labrav/common'
import { useStyles } from './ProductListing.styles'
import {
  getProducts,
  setSelectedProductOnListing,
  clearProductsListing,
} from '../../../modules/productsListing/action'
import { useDispatch, useSelector } from '../../../store'
import {
  FreeTrialStatus,
  Product,
} from '../../../modules/productsListing/types'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LaunchSharpIcon from '@mui/icons-material/LaunchSharp'
import type { ClassNameMap } from '@mui/material'
import {
  Button,
  ButtonV2,
  CloudSelector,
  ExternalLink,
  LabraToolTip,
  PlanType,
  TableComponent,
} from '@labrav/react-components'
import { Link, useNavigate, useParams } from 'react-router-dom'
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import {
  setPageNumber,
  setPageSize,
} from '../../../modules/privateOffer/action'
import { isEmpty } from 'lodash'
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined'
import { SelectChangeEvent, Typography } from '@mui/material'
import { GoLiveButton } from '../../../common/components/GoLiveButton/GoLiveButton'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { CloudType } from '../../../types'
import { getStatusAndLabelForProduct } from '../../../common/utils/getStatusAndLabelForProduct'
import { getFederatedLoginLink } from '../../../api/marketplace'
import { AxiosError, AxiosResponse } from 'axios'
import {
  EnableConcurrent,
  HardOptEnableConcurrent,
} from '../EnableConcurrent/EnableConcurrent'
import {
  MarketplaceIcon,
  type MarketplaceIconProps,
} from '../../../components/MarketplaceIcon/MarketplaceIcon'
import { ShortenedName } from '../../../common/components/ShortenedName/ShortenedName'

export { MarketplaceIcon, type MarketplaceIconProps }
export { ShortenedName }

export const mapping = {
  'Product name': 'sellerProductName',
  'Product ID': 'sellerProductId',
  Status: 'status',
  'No. of subscriptions': 'noOfActiveContracts',
  '': 'freeTrial',
  Actions: 'actions',
}
export const mappingAdminUi = {
  'Product name': 'sellerProductName',
  'Product ID': 'sellerProductId',
  Status: 'status',
  'Enable concurrent for buyer': 'enableConcurrentForBuyer',
  '': 'freeTrial',
  Actions: 'actions',
}

export const ProductListing: React.FC<{
  externalBaseURL?: string
  search?: string
  partnerId?: string
}> = ({ externalBaseURL, search, partnerId: partnerIdProp }) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()

  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    partnerId: partnerIdFromRoute,
  } = useParams<{
    cloud: string
    accountId: string
    partnerId: string
  }>()

  const accountId = useAccountId({ product: 'flyout' })
  const marketplaceCloudAccounts = useCloudAccounts('flyout')

  // Get selectedAccountIds from Redux to find the default cloud and account
  const selectedAccountIds = useSelector(
    state => state.accountSelection?.selectedAccountIds
  )
  const flyoutSelections =
    (selectedAccountIds as Record<string, unknown> | undefined)?.['FLYOUT'] ||
    selectedAccountIds?.flyout

  // Ref to read flyoutSelections without making it a useEffect dep — it changing
  // from a dispatch INSIDE the effect would otherwise re-trigger the effect
  // infinitely before React Router updates routeAccountId.
  const flyoutSelectionsRef = useRef(flyoutSelections)
  flyoutSelectionsRef.current = flyoutSelections

  const isAdmin = useUserType() === PartnerType.Admin

  const [modalProductData, setModalProductData] = useState<Product | null>(null)

  const [enableConcurrentModal, setEnableConcurrentModal] =
    useState<Product | null>(null)

  const showEnableConcurrentModal = useCallback((row: Product) => {
    setEnableConcurrentModal(row)
  }, [])

  const handleCloseEnableConcurrentModal = useCallback(() => {
    setEnableConcurrentModal(null)
  }, [])

  const showModal = useCallback((row: Product) => {
    setModalProductData(row)
  }, [])

  const handleClose = useCallback(() => {
    setModalProductData(null)
  }, [])

  const handleNoAction = () => null
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const currentAccountId = routeAccountId || accountId || ''
  const partnerId = partnerIdProp || partnerIdFromRoute || ''

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const [federatedLoginLink, setFederatedLoginLink] = useState<string | null>(
    null
  )
  const isLoadingGenerateFederatedLoginLink = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.GENERATE_FEDERATED_LOGIN_LINK)
  )

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      if (!partnerId) {
        console.error('PartnerId is not available for cloud account change')
        return
      }

      // Extract original accountId from the combined format "accountId-product-cloud"
      const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params

      const cloudPath = cloud.toLowerCase()

      const pathname = `/oppsync/admin/partners/${partnerId}/flyout/${cloudPath}/${originalAccountId}/productsListing`
      navigate(pathname, { replace: true })

      await dispatch(startLoading(LoadingTypes.GENERAL))

      await dispatch(setSelectedAccount(originalAccountId, 'flyout', cloud))

      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(setCloudMarketplaceProductListing(cloud))
      setFederatedLoginLink(null)

      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [dispatch, navigate, selectedCloudTypeProductListing, partnerId]
  )

  useEffect(() => {
    if (
      isAdmin &&
      !routeAccountId &&
      partnerId &&
      marketplaceCloudAccounts.length > 0
    ) {
      let defaultCloud: CloudType | null = null
      let defaultAccountId: string | null = null

      const currentFlyoutSelections = flyoutSelectionsRef.current
      if (
        currentFlyoutSelections &&
        typeof currentFlyoutSelections === 'object'
      ) {
        const clouds = Object.keys(currentFlyoutSelections) as CloudType[]
        if (clouds.length > 0) {
          defaultCloud = clouds[0]
          defaultAccountId =
            (currentFlyoutSelections as Record<string, string>)[defaultCloud] ||
            null
        }
      }

      if (!defaultAccountId || !defaultCloud) {
        const firstAccount = marketplaceCloudAccounts[0]
        if (firstAccount) {
          defaultCloud = firstAccount.provider as CloudType
          defaultAccountId = firstAccount.originalId
        }
      }

      if (defaultAccountId && defaultCloud) {
        const cloudPath = defaultCloud.toLowerCase()
        dispatch(setSelectedAccount(defaultAccountId, 'flyout', defaultCloud))
        dispatch(setCloudMarketplaceProductListing(defaultCloud))
        navigate(
          `/oppsync/admin/partners/${partnerId}/flyout/${cloudPath}/${defaultAccountId}/productsListing`,
          {
            replace: true,
          }
        )
      }
    }
  }, [
    marketplaceCloudAccounts,
    navigate,
    isAdmin,
    partnerId,
    routeAccountId,
    dispatch,
  ])

  useEffect(() => {
    if (currentAccountId && (routeAccountId || !isAdmin)) {
      const routeCloudUpper = routeCloud?.toUpperCase()
      const isCloudSynced =
        !routeCloudUpper || routeCloudUpper === selectedCloudTypeProductListing

      if (isCloudSynced) {
        dispatch(
          actionTypeWrapper(
            selectedCloudTypeProductListing,
            clearProductsListing()
          )
        )
        dispatch(getProducts(currentAccountId, selectedCloudTypeProductListing))
      }
      dispatch(setPageNumber(1))
      dispatch(setPageSize(10))
    }
  }, [
    currentAccountId,
    selectedCloudTypeProductListing,
    routeAccountId,
    routeCloud,
    isAdmin,
    dispatch,
  ])

  const handleOffersClick = (productId: string) => async () => {
    await dispatch(
      actionTypeWrapper(
        selectedCloudTypeProductListing,
        setSelectedProductOnListing(productId)
      )
    )
  }

  const handleGenerateLoginLink = async () => {
    await dispatch(startLoading(LoadingTypes.GENERATE_FEDERATED_LOGIN_LINK))
    try {
      const response = await getFederatedLoginLink(currentAccountId)
      const loginUrl = (response?.data as { login_url?: string } | undefined)
        ?.login_url
      if (loginUrl) {
        setFederatedLoginLink(loginUrl)
      }
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      await dispatch(
        updateAppAlert({
          message:
            'Error generating login link: ' +
            errorResponse?.data?.code +
            ' - ' +
            errorResponse?.data?.errors?.[0],
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERATE_FEDERATED_LOGIN_LINK))
    }
  }

  const renderAction = (row: Product) => {
    return (
      <div className={classes.actionsContainer}>
        {!externalBaseURL && (
          <div className={classes.actionIcon}>
            {row.numberOfContracts === 0 ? (
              <LabraToolTip
                label="No contract available"
                tooltipPlacement="top"
              >
                <ReceiptLongIcon
                  fontSize="small"
                  data-testid={`receipt-${row.productId}`}
                  className={classes.contractDisabled}
                />
              </LabraToolTip>
            ) : (
              <LabraToolTip
                label="View contracts and send metering information"
                tooltipPlacement="top"
              >
                <Link
                  to={{
                    pathname: `/flyout/products-listing/${row.productId}/contracts`,
                  }}
                  className={classes.contractLink}
                  data-id="link-to-contract"
                >
                  <ReceiptLongIcon
                    fontSize="small"
                    data-testid={`receipt-${row.productId}`}
                  />
                </Link>
              </LabraToolTip>
            )}
          </div>
        )}
        <div className={classes.actionIcon}>
          <LabraToolTip label="View Private Offers" tooltipPlacement="top">
            <Link
              to={{
                pathname: `${
                  externalBaseURL || '/flyout'
                }/offers-authorizations/private-offers`,
                search,
              }}
              state={{
                cloudMarketplace: row.cloudMarketplace,
                sellerProductName: row.productName,
              }}
              className={classes.contractLink}
              data-id="link-to-private-offer"
              onClick={handleOffersClick(row.productId)}
            >
              <HandshakeOutlined
                fontSize="small"
                data-testid={`handshake-${row.productId}`}
              />
            </Link>
          </LabraToolTip>
        </div>
        {selectedCloudTypeProductListing === 'AWS' && (
          <>
            {!externalBaseURL && (
              <div className={classes.actionIcon}>
                <LabraToolTip label="View Revenue" tooltipPlacement="top">
                  <Link
                    to={{
                      pathname: '/flyout/revenue',
                    }}
                    className={classes.contractLink}
                    data-id="link-to-revenue"
                    onClick={handleOffersClick(row.productId)}
                  >
                    <AttachMoneyIcon
                      fontSize="small"
                      data-testid={`handshake-${row.productId}`}
                    />
                  </Link>
                </LabraToolTip>
              </div>
            )}

            {!externalBaseURL && (
              <div className={classes.actionIcon}>
                {(isEmpty(row.freeTrial?.freeTrialStatus) ||
                  row.freeTrial?.freeTrialStatus ===
                    FreeTrialStatus.FREE_TRIAL_CREATED ||
                  row?.freeTrial?.freeTrialStatus ===
                    FreeTrialStatus.FREE_TRIAL_DEACTIVATED) &&
                row?.productType !== PlanType.SAAS_SUBSCRIPTION ? (
                  <LabraToolTip label="Free Trial" tooltipPlacement="top">
                    <Link
                      to={{
                        pathname: `/flyout/products-listing/${row.productId}/free-trial`,
                      }}
                      className={classes.contractLink}
                      data-id="link-to-free-trial"
                    >
                      <LocalOfferOutlined
                        fontSize="small"
                        data-testid={`free-trial-${row.productId}`}
                      />
                    </Link>
                  </LabraToolTip>
                ) : (
                  <LabraToolTip
                    label={getText(
                      row.freeTrial?.freeTrialStatus || ('' as FreeTrialStatus)
                    )}
                    tooltipPlacement="top"
                  >
                    <LocalOfferOutlined
                      fontSize="small"
                      data-testid={`free-trial-${row.productId}`}
                      className={classes.contractDisabled}
                    />
                  </LabraToolTip>
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  const renderActionAdminUi = (row: Product) => {
    const shouldDisableButton = !!row.marketplaceUrl
    const handleAddListingClick = () => {
      showModal(row)
    }

    return (
      <div className={classes.buttonContainer}>
        <GoLiveButton row={row} partnerId={currentAccountId} />
        <Button
          className={classes.submitButton}
          disabled={shouldDisableButton}
          onClick={handleAddListingClick}
          data-testid="add-listing-button"
        >
          Add listing details
        </Button>
      </div>
    )
  }

  const renderEnableConcurrentForBuyer = (productRow: Product) => {
    const handleEnableConcurrentForBuyer = () => {
      showEnableConcurrentModal(productRow)
    }

    const isHardOptShow = productRow?.concurrentEnabledBuyers

    if (isHardOptShow) {
      return (
        <HardOptEnableConcurrent
          productRow={productRow}
          currentAccountId={currentAccountId}
          selectedCloudTypeProductListing={selectedCloudTypeProductListing}
        />
      )
    }

    return (
      <Button
        className={classes.submitButton}
        onClick={handleEnableConcurrentForBuyer}
        data-testid="add-listing-button"
      >
        Enable concurrent for buyer
      </Button>
    )
  }
  const renderMarketPlace = (row: Product) => {
    const { cloudMarketplace, marketplaceUrl, productName } = row
    return (
      <div className={classes.productNameContainer}>
        <MarketplaceIcon
          classes={classes as ClassNameMap}
          cloudMarketplace={cloudMarketplace}
        />
        <ShortenedName name={productName} length={23} />
        <a target="_blank" className={classes.link} href={marketplaceUrl}>
          <LaunchSharpIcon className={classes.linkIcon} />
        </a>
      </div>
    )
  }
  const renderFreeTrialBadge = (productRow: Product) => {
    const { freeTrial } = productRow
    if (
      !isEmpty(freeTrial?.freeTrialStatus) &&
      freeTrial?.freeTrialStatus === FreeTrialStatus.FREE_TRIAL_CREATED
    )
      return (
        <div className={classes.badgeContainer}>
          <Typography className={classes.badge}>
            {'free trial active'}
          </Typography>
        </div>
      )
    return <div></div>
  }

  const customObject = productRows.map(productRow => {
    const statusLabel = getStatusAndLabelForProduct(productRow.status)
    return {
      sellerProductName: renderMarketPlace(productRow),
      sellerProductId: (
        <ShortenedName name={productRow.productId} length={23} />
      ),
      status: statusLabel.label,
      noOfActiveContracts: productRow?.numberOfActiveContracts
        ? productRow.numberOfActiveContracts.toString()
        : '-',
      freeTrial: renderFreeTrialBadge(productRow),
      actions: renderAction(productRow),
    }
  })

  const customObjectAdmin = productRows.map(productRow => {
    const statusLabel = getStatusAndLabelForProduct(productRow.status)
    return {
      sellerProductName: renderMarketPlace(productRow),
      sellerProductId: (
        <ShortenedName name={productRow.productId} length={23} />
      ),
      status: statusLabel.label,
      noOfContracts: productRow.numberOfContracts.toString(),
      freeTrial: renderFreeTrialBadge(productRow),
      enableConcurrentForBuyer: renderEnableConcurrentForBuyer(productRow),
      actions: renderActionAdminUi(productRow),
    }
  })
  const objectLabraUI = isAdmin ? customObjectAdmin : customObject

  const _cloudTypes = cloudTypes.map(cloud => {
    if (cloud.name === 'AZURE' || cloud.name === 'GCP') {
      return { ...cloud, disabled: false }
    } else {
      return cloud
    }
  })

  const handleCloudChange = async (event: SelectChangeEvent) => {
    const newCloud = event.target.value as CloudType
    if (newCloud !== selectedCloudTypeProductListing) {
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing('')
        )
      )
      await dispatch(setCloudMarketplaceProductListing(newCloud))
    }
  }
  return (
    <>
      <div
        data-testid="product-listing"
        className={`${isAdmin ? classes.containerAdmin : classes.container}`}
      >
        {isAdmin && partnerId ? (
          <div className={classes.accountContainer}>
            <div className={classes.cloudAccountContainer}>
              <CloudAccountChangeV2
                cloudAccounts={marketplaceCloudAccounts}
                selectedAccountId={selectedAccountIdWithCloud}
                onChange={handleCloudAccountChange}
                width={220}
                size="small"
              />
            </div>
            {/* AWS Generate login link button */}
            {selectedCloudTypeProductListing === CloudType.AWS && (
              <ButtonV2
                styleType="primary"
                onClick={handleGenerateLoginLink}
                loading={isLoadingGenerateFederatedLoginLink}
              >
                Generate Login Link
              </ButtonV2>
            )}
            {!!federatedLoginLink && !!federatedLoginLink?.length && (
              <ExternalLink href={federatedLoginLink} noopener noreferrer>
                Click to open
              </ExternalLink>
            )}
          </div>
        ) : !isAdmin ? (
          <div className={classes.cloudContainer}>
            <CloudSelector
              cloudTypes={_cloudTypes}
              onChange={handleCloudChange}
              selectedCloud={selectedCloudTypeProductListing}
            />
          </div>
        ) : (
          <></>
        )}
        <TableComponent
          mapping={isAdmin ? mappingAdminUi : mapping}
          objects={objectLabraUI}
          component="div"
          hidePagination={true}
          onChange={handleNoAction}
          onClick={handleNoAction}
          onChangeRowsPerPage={handleNoAction}
          noHover={true}
          rowsPerPageOptions={[]}
          pageNumber={0}
          count={0}
          rowsPerPage={0}
        />
      </div>
      {modalProductData ? (
        <AddListingModal
          modalProductData={modalProductData}
          handleClose={handleClose}
          accountId={currentAccountId}
        />
      ) : null}
      {enableConcurrentModal ? (
        <EnableConcurrent
          enableConcurrentModal={enableConcurrentModal as Product}
          handleClose={handleCloseEnableConcurrentModal}
          accountId={currentAccountId}
          cloudMarketplace={selectedCloudTypeProductListing}
        />
      ) : null}
    </>
  )
}

const getText = (status: FreeTrialStatus) => {
  switch (status) {
    case FreeTrialStatus.FREE_TRIAL_CREATION_IN_PROGRESS:
      return 'Free Trial Creation In Progress'
    case FreeTrialStatus.FREE_TRIAL_CREATED:
      return 'Free Trial Active'
    case FreeTrialStatus.FREE_TRIAL_DEACTIVATION_IN_PROGRESS:
      return 'Free Trial Deactivation In Progress'
    case FreeTrialStatus.FREE_TRIAL_DEACTIVATED:
      return 'Free Trial Deactivated'
    default:
      return 'Free trial can only be created for SAAS Contract products.'
  }
}
